'use client';

import { Avatar, AvatarImage } from '@ahafood/ui/components/avatar';
import { Card } from '@ahafood/ui/components/card';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { ArrowRight, Clock, Sparkles } from 'lucide-react';
import React, { useEffect, useState, type PropsWithChildren } from 'react';
import type { Store } from '@/api/ahafood/get-store-fn';
import { useGetStores } from '@/api/use-get-store';
import { FreeDragCarousel } from '@/components/carousel/free-drag-carousel';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAiFoodOrderStore, useAuthStore } from '@/store';

type OpenHoursType = Record<string, { start_time: number; end_time: number }[]>;

interface StoreCardProps {
  store: Store;
  index: number;
}

interface NewStoreSessionProps {
  cityId: string;
  citySlug: string;
}

export const StoreHours: React.FC<{ openHours: OpenHoursType }> = ({
  openHours,
}) => {
  const getCurrentDayHours = (openHours: OpenHoursType): string => {
    const today = new Date().getDay();
    const dayIndex = today === 0 ? 6 : today - 1;
    const hours = openHours[dayIndex.toString()];

    if (!hours || hours.length === 0) {
      return 'Đóng cửa';
    }

    const formatTime = (minutes: number): string => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    return hours
      .map(
        (range) =>
          `${formatTime(range.start_time)} - ${formatTime(range.end_time)}`
      )
      .join(', ');
  };
  const [currentDayHours, setCurrentDayHours] = useState<string>('');

  useEffect(() => {
    const updateHours = () => {
      setCurrentDayHours(getCurrentDayHours(openHours));
    };

    updateHours();
    const timer = setInterval(updateHours, 600000);

    return () => clearInterval(timer);
  }, [openHours]);

  return (
    <div className="flex flex-row text-sm text-neutral-60">
      <Clock className="mr-1 size-4 self-center" />
      <span className="items-center truncate">{currentDayHours}</span>
    </div>
  );
};

export default function NewStoreSession({
  cityId,
  citySlug,
}: NewStoreSessionProps) {
  const router = useRouter();
  const address = useAiFoodOrderStore.use.address();

  // Fetch new stores
  const { data: storesData, isLoading } = useGetStores({
    city_id: cityId,
    limit: 15,
    special_request: 'new_store',
    ...(address?.lat && address?.lng
      ? {
          lat: address.lat,
          lng: address.lng,
        }
      : {}),
  });

  const stores = storesData?.stores ?? [];

  const handleDealToggle = () => {
    router.push(`/${citySlug}/danh-sach-cua-hang-moi`);
  };

  // Show loading state — match new UI: blue→white gradient, horizontal carousel
  if (isLoading) {
    return (
      <section className="relative overflow-hidden bg-gradient-to-b from-secondary-20 to-white w-full max-w-screen-xl mx-auto md:rounded-2xl py-6 md:py-8">
        <div className="px-4 mb-4 flex items-start justify-between gap-3">
          <div className="flex-1 flex flex-col gap-1.5">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
          <Skeleton className="size-9 rounded-full" />
        </div>
        <div className="flex gap-3 md:gap-4 px-4 overflow-x-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="basis-[45%] md:basis-64 shrink-0 flex flex-col gap-2 bg-white rounded-2xl p-1.5"
            >
              <Skeleton className="aspect-square w-full rounded-xl" />
              <div className="px-1.5 pt-1 pb-1.5 flex flex-col gap-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Don't render if no stores
  if (!stores || stores.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-secondary-20 to-white w-full max-w-screen-xl mx-auto md:rounded-2xl py-6 md:py-8">
      {/* Decorative sparkles — static, không animate để giảm render */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none overflow-hidden"
      >
        <Sparkles className="absolute top-3 right-8 size-5 text-blue-30/60" />
        <Sparkles className="absolute top-10 left-[55%] size-3 text-blue-40/40" />
        <Sparkles className="absolute bottom-12 right-[15%] size-4 text-blue-30/50" />
        <Sparkles className="absolute top-1/2 left-3 size-3.5 text-blue-40/40" />
        <Sparkles className="absolute bottom-4 left-[40%] size-3 text-blue-30/50" />
      </div>

      {/* Header — gradient text title + tagline + see-all arrow (match SoloMeal/Flash Deal pattern) */}
      <div className="relative z-10 px-4 mb-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight bg-gradient-to-r from-[#1E5FD5] via-[#3B82F6] to-[#5AB8FF] bg-clip-text text-transparent">
            Quán mới
          </h2>
          <p className="text-xs md:text-sm text-blue-60 font-medium mt-0.5">
            Khám phá quán mới mở gần bạn
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            handleDealToggle();
            trackEvent('click_button', {
              action_time: Math.floor(Date.now() / 1000),
              button_name: 'new_store_click',
              button_location: 'new_store_section_header',
            });
          }}
          aria-label="Xem tất cả quán mới"
          className="shrink-0 size-9 rounded-full bg-blue-50 text-white shadow-md flex items-center justify-center hover:bg-blue-60 transition-colors"
        >
          <ArrowRight className="size-4" />
        </button>
      </div>

      <FreeDragCarousel
        className="w-full relative z-10"
        contentClassName="ml-0 gap-3 md:gap-4 px-4 items-stretch"
      >
        {stores.map((store, index) => (
          <FreeDragCarousel.Item
            key={store._id}
            className="basis-[45%] md:basis-64 pl-0"
          >
            <NewStoreItem store={store} index={index} />
          </FreeDragCarousel.Item>
        ))}

        {/* "Xem tất cả" — arrow card cuối carousel */}
        <FreeDragCarousel.Item className="basis-auto pl-0">
          <button
            type="button"
            onClick={() => {
              handleDealToggle();
              trackEvent('click_button', {
                action_time: Math.floor(Date.now() / 1000),
                button_name: 'new_store_click',
                button_location: 'new_store_section',
              });
            }}
            aria-label="Xem tất cả quán mới"
            className="h-full flex items-center justify-center px-2"
          >
            <div className="size-12 rounded-full bg-white text-blue-50 shadow-md flex items-center justify-center hover:bg-blue-50 hover:text-white transition-colors">
              <ArrowRight className="size-5" />
            </div>
          </button>
        </FreeDragCarousel.Item>
      </FreeDragCarousel>
    </section>
  );
}

export function NewStoreItem({ store, index }: StoreCardProps) {
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuthStore();

  const handleStoreClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'store_detail',
      button_location: 'new_store_section',
      storeId: store._id,
      storeSlug: store.slug,
      storeName: store.name,
      user_id: user?.cunstomerId,
    });
    router.push(routes.storeDetail(store.slug));
  };

  const imageSrc =
    store.image_url || `${assetPrefix}/static/images/empty-store.webp`;

  return (
    <Card
      onClick={handleStoreClick}
      className="group relative h-full flex flex-col bg-white border-none rounded-2xl overflow-hidden shadow-none md:shadow-sm md:hover:shadow-md md:hover:-translate-y-0.5 md:transition-all md:duration-200 cursor-pointer p-1.5"
    >
      {/* Image Section */}
      <div className="relative aspect-square overflow-hidden rounded-xl">
        <Avatar className="size-full rounded-none">
          <AvatarImage
            src={imageSrc || `${assetPrefix}/static/images/empty-store.webp`}
            alt={store.name}
            className="object-cover md:transition-transform md:duration-500 md:group-hover:scale-105"
          />
        </Avatar>
      </div>

      {/* Content Section — compact */}
      <div className="px-1.5 pt-2 pb-1.5 flex flex-col gap-1">
        <h3 className="text-[13px] md:text-sm font-semibold text-neutral-90 line-clamp-1 leading-tight">
          {store.name}
        </h3>
        <div className="flex items-center text-[11px] text-neutral-50 font-normal">
          <Clock className="mr-1 size-3" />
          <span className="truncate">
            {(() => {
              const today = new Date().getDay();
              const dayIndex = today === 0 ? 6 : today - 1;
              const hours = store.open_hours[dayIndex.toString()];

              if (!hours || hours.length === 0) {
                return 'Đóng cửa';
              }

              const formatTime = (minutes: number): string => {
                const h = Math.floor(minutes / 60);
                const m = minutes % 60;
                return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
              };

              const firstSlot = hours[0];
              const lastSlot = hours[hours.length - 1];

              return `${formatTime(firstSlot.start_time)} - ${formatTime(lastSlot.end_time)}`;
            })()}
          </span>
        </div>
      </div>
    </Card>
  );
}

export function StoreListWrapper({ children }: PropsWithChildren) {
  return (
    <section
      className="ahf-deal relative overflow-hidden bg-transparent"
      data-testid="home-new-store-section"
    >
      <div
        className={cn(
          'relative z-[1] mx-auto max-w-screen-xl gap-y-6 px-4 md:px-0 md:py-8'
        )}
      >
        {children}
      </div>
    </section>
  );
}
